import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import Navbar from '../components/Navbar';
import { theme } from '../utils/theme';

const Login = () => {
  const { t, i18n } = useTranslation('login');
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    
    // Clear email verification error when email is changed
    if (e.target.name === 'email' && emailNotVerified) {
      setEmailNotVerified(false);
      setVerificationSent(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);

    try {
      const response = await api.post('/api/auth/login', formData);
      
      if (response.data.token) {
        // Store user data and token
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userData', JSON.stringify(response.data.user));
        
        // Set user's preferred language if available
        if (response.data.user.defaultLanguage) {
          i18n.changeLanguage(response.data.user.defaultLanguage);
        }
        
        window.dispatchEvent(new Event('authChange'));
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('errors.failed');
      setError(errorMessage);
      
      // Check if the error is because email is not verified
      if (errorMessage === 'Email not verified') {
        setEmailNotVerified(true);
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendingVerification(true);
      const response = await api.post('/api/auth/resend-verification', { 
        email: formData.email 
      });
      
      setVerificationSent(true);
      setResendingVerification(false);
    } catch (err) {
      setError(err.response?.data?.error || t('errors.verificationFailed'));
      setResendingVerification(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto bg-[#0C3B2E] text-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold">
                {t('title')}
              </h2>
              <p className="mt-2 text-sm text-white/80">
                {t('newUser')}{' '}
                <Link to="/register" className="font-medium text-[#FFBA00] hover:text-white">
                  {t('createAccount')}
                </Link>
              </p>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="off">
              {error && !emailNotVerified && (
                <div className="bg-red-900/50 border-l-4 border-red-400 p-4 rounded" role="alert">
                  <p className="text-sm text-white">{error}</p>
                </div>
              )}

              {emailNotVerified && (
                <div className="bg-yellow-900/50 border-l-4 border-yellow-400 p-4 rounded" role="alert">
                  <p className="text-sm text-white">{t('errors.emailNotVerified')}</p>
                  {!verificationSent ? (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      className="mt-2 text-sm text-[#FFBA00] hover:text-white focus:outline-none"
                    >
                      {resendingVerification ? t('resendingVerification') : t('resendVerification')}
                    </button>
                  ) : (
                    <p className="mt-2 text-sm text-green-300">{t('verificationSent')}</p>
                  )}
                </div>
              )}

              {location.state?.message && (
                <div className={`bg-green-900/50 border-l-4 border-green-400 p-4 rounded`} role="alert">
                  <p className="text-sm text-white">{location.state.message}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium">
                    {t('labels.email')}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className="mt-1 block w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6D9773] focus:border-transparent"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium">
                    {t('labels.password')}
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    className="mt-1 block w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#6D9773] focus:border-transparent"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="text-sm">
                  <Link 
                    to="/forgot-password" 
                    className="font-medium text-[#FFBA00] hover:text-white py-1 px-2 rounded-md transition-colors duration-200 inline-block border border-transparent hover:border-white/20"
                  >
                    {t('forgotPassword')}
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  className={theme.button.base + ' ' + theme.button.cta + ' w-full ' + theme.button.sizes.lg}
                >
                  {t('buttons.signIn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;